import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { Component, input, OnDestroy, OnInit, computed } from '@angular/core';

import { ModalsService } from '@core/services/modals/modals.service';
import { ResetStateService } from '@core/services/reset-state.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';

import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';

import { UserKeys } from '@config/keys/user.keys';
import { DoodUserModel } from '@store/authentication/authentication.model';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { WaitingModalComponent } from '@shared/modals/waiting-modal/waiting-modal.component';
import { AuthenticationUserJourneyType } from '@shared/blocks/authentication-block/authentication-user-journey-type';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import {
  authenticationModalResolveConfig,
  type AuthenticationModalConfig,
} from '@front-platform/content-builder';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';

export interface AuthenticationModalRuntimeConfig {
  'signup_footer'?: unknown;
}

export type AuthenticationModalConfigInput = Partial<
  Omit<AuthenticationModalConfig, keyof AuthenticationModalRuntimeConfig> &
    AuthenticationModalRuntimeConfig
>;
export type AuthenticationModalResolvedConfig = Omit<
  AuthenticationModalConfig,
  keyof AuthenticationModalRuntimeConfig
> &
  Partial<AuthenticationModalRuntimeConfig>;
export interface AuthenticationModalContract extends ConfigComponentContract<
  AuthenticationModalConfigInput,
  AuthenticationModalResolvedConfig
> {}

@Component({
  selector: 'app-authentication-modal',
  templateUrl: './authentication-modal.component.html',
  standalone: false,
})
export class AuthenticationModalComponent
  extends ModalScrollBlockBase
  implements OnInit, OnDestroy, AuthenticationModalContract
{
  private _destroyerRef = new Subject<boolean>();

  static handle = 'authentication-modal';

  readonly config = input<AuthenticationModalConfigInput>({});

  readonly userJourneyType = computed(
    () => this.resolvedConfig()['user_journey_type'] as unknown as AuthenticationUserJourneyType,
  );
  readonly legalCheckboxLabel = computed(
    () => this.resolvedConfig()['legal_checkbox_label'] as unknown as string | undefined,
  );
  readonly optinCheckboxLabel = computed(
    () => this.resolvedConfig()['optin_checkbox_label'] as unknown as string | undefined,
  );
  readonly firstStepHeading = computed(
    () => this.resolvedConfig()['first_step_heading'] as unknown as string | undefined,
  );
  readonly firstStepLoginButton = computed(
    () => this.resolvedConfig()['first_step_login_button'] as unknown as string | undefined,
  );
  readonly firstStepSignupButton = computed(
    () => this.resolvedConfig()['first_step_signup_button'] as unknown as string | undefined,
  );
  readonly loginStepHeading = computed(
    () => this.resolvedConfig()['login_step_heading'] as unknown as string | undefined,
  );
  readonly signupStepHeading = computed(
    () => this.resolvedConfig()['signup_step_heading'] as unknown as string | undefined,
  );
  readonly signupStepSubheading = computed(
    () => this.resolvedConfig()['signup_step_subheading'] as unknown as string | undefined,
  );
  readonly signupStepDisplayFirstname = computed(
    () => this.resolvedConfig()['signup_step_display_firstname'] as unknown as boolean,
  );
  readonly signupStepFirstnameLabel = computed(
    () => this.resolvedConfig()['signup_step_firstname_label'] as unknown as string | undefined,
  );
  readonly signupStepFirstnameHelp = computed(
    () => this.resolvedConfig()['signup_step_firstname_help'] as unknown as string | undefined,
  );
  readonly signupStepDisplayLastname = computed(
    () => this.resolvedConfig()['signup_step_display_lastname'] as unknown as boolean,
  );
  readonly signupStepLastnameLabel = computed(
    () => this.resolvedConfig()['signup_step_lastname_label'] as unknown as string | undefined,
  );
  readonly signupStepLastnameHelp = computed(
    () => this.resolvedConfig()['signup_step_lastname_help'] as unknown as string | undefined,
  );
  readonly signupStepEmailLabel = computed(
    () => this.resolvedConfig()['signup_step_email_label'] as unknown as string | undefined,
  );
  readonly signupStepEmailHelp = computed(
    () => this.resolvedConfig()['signup_step_email_help'] as unknown as string | undefined,
  );
  readonly signupStepDisplayPhone = computed(
    () => this.resolvedConfig()['signup_step_display_phone'] as unknown as boolean,
  );
  readonly signupStepPhoneLabel = computed(
    () => this.resolvedConfig()['signup_step_phone_label'] as unknown as string | undefined,
  );
  readonly signupStepPhoneHelp = computed(
    () => this.resolvedConfig()['signup_step_phone_help'] as unknown as string | undefined,
  );
  readonly signupStepPasswordLabel = computed(
    () => this.resolvedConfig()['signup_step_password_label'] as unknown as string | undefined,
  );
  readonly signupStepPasswordHelp = computed(
    () => this.resolvedConfig()['signup_step_password_help'] as unknown as string | undefined,
  );
  readonly signupFooter = computed(
    () => this.resolvedConfig()['signup_footer'] as unknown as unknown,
  );
  readonly authenticationProviderGoogle = computed(
    () => this.resolvedConfig()['authentication_provider_google'] as unknown as boolean,
  );
  readonly authenticationProviderFacebook = computed(
    () => this.resolvedConfig()['authentication_provider_facebook'] as unknown as boolean,
  );
  readonly authenticationProviderApple = computed(
    () => this.resolvedConfig()['authentication_provider_apple'] as unknown as boolean,
  );
  readonly cgvUrl = computed(
    () => this.resolvedConfig()['cgv_url'] as unknown as string | undefined,
  );

  readonly resolvedConfig = computedResolvedConfig<
    AuthenticationModalConfig,
    AuthenticationModalRuntimeConfig
  >(authenticationModalResolveConfig, () => ({ config: this.config() }));
  readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );

  userKeys = UserKeys;
  user: DoodUserModel | null = null;

  user$ = this.authSelector.selectUser$;
  index$: Observable<number> = this.modalSelector
    .selectModal(AuthenticationModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  isMobile$ = this.settingsSelector.selectDeviceIsMobileScreen;

  constructor(
    readonly store: Store,
    readonly authSelector: AuthStoreSelector,
    readonly modalSelector: ModalStoreSelector,
    private readonly modalsService: ModalsService,
    readonly settingsSelector: SettingsStoreSelector,
    private readonly resetStateService: ResetStateService,
    private readonly cb2PreviewState: Cb2PreviewStateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.user$
      .pipe(
        takeUntil(this._destroyerRef),
        tap(user => {
          const isFullyLoggedIn = !!(user?.[UserKeys.Id] && user?.[UserKeys.Email]);
          // Signup/login updates the store before close(); dismiss instead of showing
          // the "already logged in" screen. Defer close so we don't mutate the modal
          // store mid change-detection (NG0100 / detectChangesInComponent).
          if (isFullyLoggedIn && !this.cb2PreviewState.isActive()) {
            queueMicrotask(() => this.close());
            return;
          }
          this.user = user;
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
    super.ngOnDestroy();
  }

  close(): void {
    this.modalsService.close(AuthenticationModalComponent.handle);
  }

  resolveUserJourneyType(
    journeyType: AuthenticationModalConfig['user_journey_type'],
  ): AuthenticationUserJourneyType {
    return journeyType === AuthenticationUserJourneyType.ASK_LOGIN_OR_SIGNUP
      ? AuthenticationUserJourneyType.ASK_LOGIN_OR_SIGNUP
      : AuthenticationUserJourneyType.ASK_EMAIL_FIRST;
  }

  async logout(): Promise<void> {
    this.modalsService.open(WaitingModalComponent.handle);
    this.resetStateService.refreshUI().subscribe();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(AuthenticationModalComponent, { 'keyAliases': {} });
